// Engine core: decisions, the generator runner and logging.
//
// All rules are written as generator functions (`Gen`). Whenever a player has to
// decide something the rules `yield` a Decision; the runner pauses until every
// player listed in `decision.players` has answered, then resumes the generator
// with the answers. A game is therefore fully determined by its config (which
// includes the RNG seed) plus the ordered list of answers, which is what backups
// store and what undo replays.

import type { SpaceId } from "./board";
import { nextRandom, randomInt, shuffleInPlace } from "./rng";
import { kEventFlavor, type FlavorKey } from "./data/flavor";
import type { GameEvent, GameOver, GameState } from "./state";
import type { Seat } from "./types";

export type DecisionKind =
  | "action"      // choose an action for the action step
  | "choose"      // generic single / multi choice
  | "cards"       // pick skill cards from hand
  | "order"       // order a list (answer = permutation of option ids)
  | "window"      // response window: use an ability or pass
  | "idle"        // masked idle window, auto-answered by the server
  | "contribute"; // add cards to a skill check

export interface Option {
  id: string;
  label: string;
  detail?: string;
  /** Optional grouping for UI (e.g. "移动", "攻击"). */
  group?: string;
  space?: SpaceId;
  /** Skill card uid / item def etc. for richer rendering. */
  cardUid?: number;
  cardDef?: string;
  /** The option is this (face-up to the chooser) mythos / waypoint / spell card: `cardDef` is its id. */
  cardKind?: "mythos" | "waypoint" | "spell";
}

/** The die behind a before / after roll response window (public information). */
export interface DieInfo {
  phase: "before" | "after";
  reason: string;
  /** What the result means, e.g. "≥4：命中…" or a face → space mapping (see rollDie hints). */
  hint: string | null;
  modifier: number;
  /** A face fixed in advance (Perfect Number). */
  setTo: number | null;
  /** After the roll: the face rolled and the final value (with the modifier, 1..8). */
  raw: number | null;
  value: number | null;
}

export interface Decision {
  id: number;
  kind: DecisionKind;
  /** Seats that must answer. More than one means a simultaneous decision. */
  players: Seat[];
  /** Shown to the deciding players. May contain hidden information. */
  prompt: string;
  /** Shown to everyone else. */
  publicText: string;
  /** Shared options. Ignored for a seat when `perPlayer[seat]` exists. */
  options: Option[];
  perPlayer?: Record<number, Option[]>;
  min: number;
  max: number;
  /** Private cards the deciders are allowed to see while deciding (e.g. "look at"). */
  peek?: { label: string; cardDefs: string[] }[];
  /** Response windows around a die roll carry the die so the client can draw it. */
  die?: DieInfo;
}

/** One answer per deciding seat: the chosen option ids. */
export type AnswerMap = Record<number, string[]>;

export type Gen<T = void> = Generator<Decision, T, AnswerMap>;

export class GameEnded extends Error {
  constructor(public readonly over: Omit<GameOver, "winners" | "loyalties">) {
    super(over.detail);
  }
}

export class Engine {
  /** Set while Edmund Mallory's "Spread Misfortune" resolves a mythos card. */
  misfortune: Seat | null = null;

  constructor(public s: GameState) { }

  // ----- randomness -----
  random(): number { return nextRandom(this.s); }
  randomInt(n: number): number { return randomInt(this.s, n); }
  shuffle<T>(arr: T[]): T[] { return shuffleInPlace(this.s, arr); }

  // ----- logging -----
  log(text: string, to?: Seat[]): void {
    this.s.log.push({ seq: ++this.s.seq, text, to });
  }

  /**
   * Atmospheric narration for an event. The variant is picked from the log sequence
   * number (no game RNG is consumed, so replays and old backups are unaffected).
   */
  flavor(key: FlavorKey | string[], vars: Record<string, string> = {}): void {
    const list = Array.isArray(key) ? key : kEventFlavor[key];
    if (!list || list.length === 0) return;
    const line = list[(this.s.seq * 2654435761 >>> 0) % list.length].replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");
    this.s.log.push({ seq: ++this.s.seq, text: line, flavor: true });
  }

  /** A single specific flavor line (mythos / waypoint / spell). */
  flavorLine(line: string | undefined): void {
    if (line) this.s.log.push({ seq: ++this.s.seq, text: line, flavor: true });
  }

  event(ev: GameEvent): void {
    this.s.events.push({ seq: ++this.s.seq, ev });
  }

  playerName(seat: Seat): string {
    return this.s.players[seat].name;
  }

  // ----- decisions -----
  *ask(d: Omit<Decision, "id">): Gen<AnswerMap> {
    if (d.players.length === 0) return {};
    const answers = yield { ...d, id: ++this.s.decisionSeq };
    return answers;
  }

  /** Single choice for one seat. Returns the option id. */
  *choose(seat: Seat, prompt: string, options: Option[], publicText?: string): Gen<string> {
    if (options.length === 0) throw new Error(`no options for: ${prompt}`);
    const a = yield* this.ask({
      kind: "choose", players: [seat], prompt, publicText: publicText ?? `${this.playerName(seat)} 正在选择：${prompt}`,
      options, min: 1, max: 1,
    });
    return a[seat][0];
  }

  /** Multi choice for one seat. */
  *chooseMany(seat: Seat, prompt: string, options: Option[], min: number, max: number, publicText?: string): Gen<string[]> {
    if (max === 0 || options.length === 0) return [];
    const a = yield* this.ask({
      kind: "choose", players: [seat], prompt, publicText: publicText ?? `${this.playerName(seat)} 正在选择：${prompt}`,
      options, min, max: Math.min(max, options.length),
    });
    return a[seat];
  }

  *confirm(seat: Seat, prompt: string, yes = "是", no = "否"): Gen<boolean> {
    const r = yield* this.choose(seat, prompt, [{ id: "yes", label: yes }, { id: "no", label: no }]);
    return r === "yes";
  }

  /** Order a list of ids (answer is a permutation). */
  *order(seat: Seat, prompt: string, options: Option[]): Gen<string[]> {
    if (options.length <= 1) return options.map(o => o.id);
    const a = yield* this.ask({
      kind: "order", players: [seat], prompt, publicText: `${this.playerName(seat)} 正在决定顺序：${prompt}`,
      options, min: options.length, max: options.length,
    });
    return a[seat];
  }
}

/** Validate a seat's answer against a decision. Returns an error string or null. */
export function validateAnswer(d: Decision, seat: Seat, choice: string[]): string | null {
  if (!d.players.includes(seat)) return "不是你的决策";
  const opts = d.perPlayer?.[seat] ?? d.options;
  const ids = new Set(opts.map(o => o.id));
  if (choice.length < d.min || choice.length > d.max) return `需要选择 ${d.min}~${d.max} 项`;
  const seen = new Set<string>();
  for (const c of choice) {
    if (!ids.has(c)) return `无效选项: ${c}`;
    if (seen.has(c)) return `重复选项: ${c}`;
    seen.add(c);
  }
  return null;
}
